import {
  IWebflowPartner,
  IWebflowPartnerSignUpTypes
} from "@wingspanhq/cms/dist/lib/interfaces/webflow/partners";
import { useIsDesktop } from "@wingspanhq/fe-component-library";
import {
  IActivityContext,
  IGrantedSubscriberResponse,
  IGrowthSourceNames,
  IMember,
  IMemberProfileWrite,
  INewUser,
  PhoneVerificationStatus,
  SubscriptionPackage,
  SubscriptionPackageTier,
  SubscriptionStatus,
  SubscriptionTerm
} from "@wingspanhq/users/dist/lib/interfaces";
import React, { useEffect, useState } from "react";
import { QueryStatus } from "react-query";
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom";
import { NEC1099Filing } from "../../1099NECFiling";
import { Admin } from "../../Admin";
import { BenefitsRoutes } from "../../Benefits";
import { BookkeepingRoutes } from "../../Bookkeeping/index";
import { MemberDashboard } from "../../Dashboard/screens/MemberDashboard";
import { Documents } from "../../Documents/screens/Documents";
import { useLocalStorageState } from "../../hooks/useLocalStorage";
import { Invoices } from "../../Invoices";
import { VerificationUpgradeModal } from "../../Invoices/components/VerificationUpgrade/VerificationUpgrade";
import { DebitCardsList } from "../../Invoices/screens/cards/DebitCardsList";
import { KnowledgeCenterRoot } from "../../KnowledgeCenter";
import { BeginEmailVerification } from "../../MemberOnboarding/screens/BeginEmailVerification";
import { CompleteEmailVerification } from "../../MemberOnboarding/screens/CompleteEmailVerification";
import { EmailMagicLink2FAInstructions } from "../../MemberOnboarding/screens/EmailMagicLink2FAInstructions";
import { EmailMagicLinkHandler } from "../../MemberOnboarding/screens/EmailMagicLinkHandler";
import { OnboardingBlockForm } from "../../MemberOnboarding/screens/OnboardingBlockForm";
import { OnboardingCustomizing } from "../../MemberOnboarding/screens/OnboardingCustomizing";
import { OnboardingEmailVerification } from "../../MemberOnboarding/screens/OnboardingEmailVerification";
import { OnboardingStep1 } from "../../MemberOnboarding/screens/OnboardingStep1";
import { OnboardingStep2 } from "../../MemberOnboarding/screens/OnboardingStep2";
import { OnboardingStep3Benefits } from "../../MemberOnboarding/screens/OnboardingStep3Benefits";
import { OnboardingStep3Bookkeeping } from "../../MemberOnboarding/screens/OnboardingStep3Bookkeeping";
import { OnboardingStep3Payments } from "../../MemberOnboarding/screens/OnboardingStep3Payments";
import { OnboardingStep4Benefits } from "../../MemberOnboarding/screens/OnboardingStep4Benefits";
import { OnboardingStep4Bookkeeping } from "../../MemberOnboarding/screens/OnboardingStep4Bookkeeping";
import { OnboardingStep4Payments } from "../../MemberOnboarding/screens/OnboardingStep4Payments";
import { Select2FAChannelFormModal } from "../../MemberOnboarding/screens/Select2FAChannelForm";
import { VerifyOTP } from "../../MemberOnboarding/screens/VerifyOTP";
import { VerifyPhoneNumber } from "../../MemberOnboarding/screens/VerifyPhoneNumber";
import { VerifyPhoneNumberExistingAccount } from "../../MemberOnboarding/screens/VerifyPhoneNumberExistingAccount";
import { PersonalPayLinkRoutes } from "../../PersonalPayLink";
import {
  useCMSWebflowEnterprise,
  useCMSWebflowPartner
} from "../../query/cms/queries";
import { useUserId } from "../../query/hooks/helpers";
import { useFeatureFlags } from "../../query/hooks/useFeatureFlags";
import { useInvoicesQuery } from "../../query/payments/queries";
import {
  getCurrentEntepriseId,
  getShouldForceToEnablePayments,
  getShouldForceToOnboard1099
} from "../../query/platform/selectors";
import { useCreateOrUpdateSubscriptionV3 } from "../../query/subscriptions/mutations";
import { useSubscriptionV3Query } from "../../query/subscriptions/queries";
import { useUserLocation } from "../../query/user";
import {
  useCreateClient,
  useCreateMember,
  useUpdateActivityById
} from "../../query/users/mutations";
import {
  useActivities,
  useClientQuery,
  useMemberProfile,
  useUserProfile
} from "../../query/users/queries";
import { RenderDefaultPageErrorMessage, WSQueries } from "../../query/WSQuery";
import { PaymentsSecurityScreen } from "../../screens/PaymentsSecurity";
import {
  getAdminSessionToken,
  getRequestingUserSessionToken
} from "../../services/sessionStorage";
import { usersService } from "../../services/users";
import { MemberBankStatementModal } from "../../Settings/components/MemberBankStatementModal/MemberBankStatementModal";
import { AddNewToken } from "../../Settings/screens/AddNewToken";
import { EditNewToken } from "../../Settings/screens/EditNewToken";
import { MemberBankStatements } from "../../Settings/screens/MemberBankStatements";
import { MembershipBillingHistory } from "../../Settings/screens/MembershipBillingHistory";
import { MembershipPlansWithAddOns } from "../../Settings/screens/MembershipPlansWithAddOns";
import { MembershipTrialSuccess } from "../../Settings/screens/MembershipTrialSuccess";
import { NextGenSubscriptionPlans } from "../../Settings/screens/NextGenSubscriptionPlans";
import { SettingsMain as Settings } from "../../Settings/screens/SettingsMain";
import {
  hasAdminScope,
  hasDocumentsScope,
  hasFinancesScope,
  hasPaymentsScope
} from "../../Settings/utils/teamUtils";
import { useSetWSStore, useWSStore } from "../../store";
import { Taxes } from "../../Taxes/screens/Taxes";
import { isUnderWriterMember } from "../../utils/growthAttribution";
import { useDidFinishQueries } from "../../utils/useDidFinishQueries";
import { MemberPlaidModals } from "../../utils/usePlaidLink";
import { WalletIndex } from "../../Wallet/screens/WalletIndex";
import { BaseAppLayout } from "../BaseAppLayout/BaseAppLayout.component";
import { LoadingPlaceholder } from "../LoadingPlaceholder";
import { SessionOTPModal } from "../SessionOTPModal/SessionOTPModal";
import { OrganizationIndex } from "../../Organization";
import { useResume1099OnboardingFlow } from "../../hooks/useResumeSetupFlow";
import { useEnterpriseQuery } from "../../query/platform/queries";

const goToFirstAuthorizedAccount = async (
  userId: string,
  history: any
): Promise<boolean> => {
  try {
    const authorizedAccounts = await usersService.authorization.accounts(
      userId
    );

    if (authorizedAccounts.length > 0) {
      const authorizedAccount = authorizedAccounts[0];

      await usersService.session.requestPrincipalUser({
        email: authorizedAccount.accessibleMember.user.email,
        captchaToken: await window.grecaptcha.execute(
          process.env.REACT_APP_GOOGLE_RECAPTCHA_V3_SITE_KEY || "",
          {
            action: "reauth"
          }
        ),
        captchaVersion: "3"
      });
      if (
        hasAdminScope(authorizedAccount.allowedScopeGroupIds) ||
        hasPaymentsScope(authorizedAccount.allowedScopeGroupIds)
      ) {
        history.push("/member/invoices");
      } else if (hasFinancesScope(authorizedAccount.allowedScopeGroupIds)) {
        history.push("/member/bookkeeping");
      } else if (hasDocumentsScope(authorizedAccount.allowedScopeGroupIds)) {
        history.push("/member/documents");
      }
      window.location.reload();
      return true;
    } else {
      throw new Error("No authorized accounts found");
    }
  } catch (error) {
    console.error("Error while teams invite flow", error);
    // Catch and track error and continue REGULAR flow
    return false;
  }
};

type Props = {};

const SignInRedirect: React.FC<RouteComponentProps> = props => {
  const userId = useUserId();
  const location = useLocation();
  const invoicesQuery = useInvoicesQuery();
  const userProfileQuery = useUserProfile(userId);
  return (
    <WSQueries queries={{ invoicesQuery, userProfileQuery }}>
      {({
        invoicesQuery: { data: invoices },
        userProfileQuery: { data: user }
      }) => {
        if (props.location.hash) {
          return <Redirect to={props.location.hash.slice(1)} />;
        } else if (invoices.length > 0) {
          return <Redirect to="/member/invoices" />;
        } else if (shouldEnforcePhoneVerification(user)) {
          return (
            <Redirect to={`/member/verify/phone-number${location.hash}`} />
          );
        }
        return <Redirect to="/member/dashboard" />;
      }}
    </WSQueries>
  );
};

const shouldEnforcePhoneVerification = (user: INewUser) => {
  // We are enforcing the users who are signed up after Jun 30, 2021.
  return (
    user?.createdAt > new Date(2021, 5, 30) &&
    user?.phone.status !== PhoneVerificationStatus.Verified
  );
};

const CreateMember: React.FC<{ onFinish: () => void }> = ({ onFinish }) => {
  const location = useLocation();
  const [freelanceType] = useLocalStorageState("wingspan:pre-sign-up:q1", "");
  const [productImportance] = useLocalStorageState(
    "wingspan:pre-sign-up:q2",
    ""
  );
  const [productAspectImportance] = useLocalStorageState(
    "wingspan:pre-sign-up:q3",
    ""
  );
  const [signUpPartner] = useLocalStorageState(
    "wingspan:sign-up-with-partner",
    ""
  );
  const featureFlagsQuery = useFeatureFlags();
  const qPartner = useCMSWebflowPartner(signUpPartner, {
    enabled: signUpPartner
  });

  const store = useWSStore();
  const setStore = useSetWSStore();
  const userId = useUserId();
  const history = useHistory();
  const activityQuery = useActivities(userId);
  const userProfileQuery = useUserProfile(userId);
  const clientQuery = useClientQuery(userId);
  const [updateActivity] = useUpdateActivityById();

  const locationQuery = useUserLocation();

  const [createMember] = useCreateMember(userId);
  const [createClient] = useCreateClient(userId);
  const [createOrUpdateSubscription] = useCreateOrUpdateSubscriptionV3(userId);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const stuckQuery = [activityQuery, userProfileQuery].find(
        query => query.status === QueryStatus.Loading
      );

      if (stuckQuery) {
        stuckQuery.refetch();
      }
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [activityQuery, userProfileQuery]);

  useDidFinishQueries(
    [
      activityQuery,
      locationQuery,
      userProfileQuery,
      featureFlagsQuery,
      clientQuery
    ],
    async () => {
      // Setting pre sign-up questionnaire responses in member profile
      let memberProfile: IMemberProfileWrite = {};
      if (freelanceType) {
        memberProfile.freelanceType = freelanceType;
      }
      if (productImportance) {
        memberProfile.productImportance = [productImportance];
      }
      if (productAspectImportance) {
        memberProfile.productAspectImportance = [productAspectImportance];
      }
      const member = await createMember({
        userId,
        profile: memberProfile
      });

      if (!clientQuery.data) {
        createClient({ userId });
      }

      const partner = qPartner.data as IWebflowPartner;

      // Handle subscription creation
      if (store.isEmployerSignUp) {
        // Sign up all employers with Corporate plan by default
        const employerSubPlan = {
          package: SubscriptionPackage.Professional,
          packageTier: SubscriptionPackageTier.Basic,
          discountCode: "ENTERPRISEOFFPLATFORMBILLING",
          term: SubscriptionTerm.Yearly
        };
        await createOrUpdateSubscription(employerSubPlan);
      } else if (store.discountCode || partner?.stripeDiscountCode) {
        // Apply discount code if it presents
        await createOrUpdateSubscription({
          discountCode: store.discountCode ?? partner?.stripeDiscountCode
        });
      }

      if (
        store.signUpType === IWebflowPartnerSignUpTypes.CollaboratorPayments ||
        store.signUpType === IWebflowPartnerSignUpTypes.BenefitsOnly ||
        store.growthAttributionDetails
      ) {
        await updateActivity({
          userId,
          events: {
            benefitsOnlySignup:
              store.signUpType === IWebflowPartnerSignUpTypes.BenefitsOnly
                ? true
                : undefined
          },
          context: {
            growthSource: (store.growthAttributionDetails
              ?.growthSource as unknown) as IGrowthSourceNames,
            growthSourceName: store.growthAttributionDetails?.growthName
          }
        });
      }

      if (store.signUpType === IWebflowPartnerSignUpTypes.TeamsInvite) {
        const success = await goToFirstAuthorizedAccount(userId, history);
        if (success) {
          return;
        }
      }

      const onboardingFlow = activityQuery.data?.flows["onboarding"];
      if (
        !onboardingFlow &&
        locationQuery.data &&
        locationQuery.data.isoCode !== "US"
      ) {
        history.replace("/member/onboarding/block");
      } else if (
        !userProfileQuery.data?.phone?.status ||
        userProfileQuery.data?.phone?.status === PhoneVerificationStatus.Pending
      ) {
        history.replace(`/member/verify/phone-number${location.hash}`);
      } else if (store.signUpType === IWebflowPartnerSignUpTypes.BenefitsOnly) {
        setStore({ productActivationAfterOnboarding: true });
        history.replace("/member/benefits");
      } else {
        setStore({ productActivationAfterOnboarding: true });
        history.replace("/member/invoices");
      }

      onFinish();
    }
  );

  return <LoadingPlaceholder />;
};

const InitMember: React.FC<{ onFinish: () => void }> = ({ onFinish }) => {
  const userId = useUserId();
  const history = useHistory();
  const location = useLocation();
  const userProfileQuery = useUserProfile(userId);
  const memberQuery = useMemberProfile(userId);
  const clientQuery = useClientQuery(userId);
  const store = useWSStore();
  const activityQuery = useActivities(userId);
  const [updateActivity] = useUpdateActivityById();
  const subscriptionQuery = useSubscriptionV3Query(userId);
  const [createOrUpdateSubscription] = useCreateOrUpdateSubscriptionV3(userId);
  const [createClient] = useCreateClient(userId);
  const enterpriseQuery = useEnterpriseQuery();
  const {
    resume: resume1099OnboardingFlow,
    flow: onboarding1099Flow
  } = useResume1099OnboardingFlow();

  useEffect(() => {
    const timeout = setTimeout(() => {
      const stuckQuery = [
        memberQuery,
        activityQuery,
        subscriptionQuery,
        userProfileQuery,
        clientQuery,
        enterpriseQuery
      ].find(query => query.status === QueryStatus.Loading);

      if (stuckQuery) {
        stuckQuery.refetch();
      }
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    memberQuery.status,
    activityQuery.status,
    subscriptionQuery.status,
    userProfileQuery.status,
    clientQuery.status,
    enterpriseQuery.status
  ]);

  useDidFinishQueries(
    [
      memberQuery,
      activityQuery,
      subscriptionQuery,
      userProfileQuery,
      clientQuery,
      enterpriseQuery
    ],
    async () => {
      if (!clientQuery.data) {
        createClient({ userId });
      }

      let recentActivity = activityQuery.data;

      // First register the user's growth source if not the default
      // This will also create a subscription grant for them if they are
      // signing up with a partner link that has the grant configuration
      if (
        store.signUpType === IWebflowPartnerSignUpTypes.CollaboratorPayments ||
        store.signUpType === IWebflowPartnerSignUpTypes.BenefitsOnly ||
        store.growthAttributionDetails
      ) {
        recentActivity = await updateActivity({
          userId,
          events: {
            benefitsOnlySignup:
              store.signUpType === IWebflowPartnerSignUpTypes.BenefitsOnly
                ? true
                : undefined
          },
          context: {
            growthSource: (store.growthAttributionDetails
              ?.growthSource as unknown) as IGrowthSourceNames,
            growthSourceName: store.growthAttributionDetails?.growthName
          }
        });
      }

      /**
       * Apply subscription grant for member if
       * 1. (subscription is in "trialing" OR member has no subscription) AND
       * 2. member has at least one granted subscription AND
       * 3. subscription grant is not applied earlier.
       */

      let grantedSubscriberList: IGrantedSubscriberResponse[] = [];
      try {
        grantedSubscriberList = await usersService.grantedSubscriber.list();
      } catch (e) {}
      const userSubscriptionGrants =
        grantedSubscriberList?.filter(sg => sg.memberId === userId) || [];
      if (
        (subscriptionQuery.data?.status === SubscriptionStatus.trialing ||
          !subscriptionQuery.data) &&
        userSubscriptionGrants.length > 0 &&
        !subscriptionQuery.data?.subscriptionGrantId
      ) {
        await createOrUpdateSubscription({
          subscriptionGrantId: userSubscriptionGrants[0].subscriptionGrantId
        });
      }

      if (store.signUpType === IWebflowPartnerSignUpTypes.TeamsInvite) {
        goToFirstAuthorizedAccount(userId, history);
      } else if (
        shouldEnforcePhoneVerification(userProfileQuery.data as INewUser)
      ) {
        history.replace(`/member/verify/phone-number${location.hash}`);
      } else if (
        enterpriseQuery.data?.is1099FilingOnly &&
        getShouldForceToOnboard1099(recentActivity)
      ) {
        resume1099OnboardingFlow();
      } else if (
        !enterpriseQuery.data?.is1099FilingOnly &&
        getShouldForceToEnablePayments(recentActivity)
      ) {
        // NOTE: 1099 onboarding users are redirected to payments setup flow
        // if the page is reloaded. For now, I have added an extra condition(!is1099FilingOnly)
        // to fix it.
        history.replace("/member/invoices/set-up");
      }

      const canSkip = window.sessionStorage.getItem("SKIP_PHONE_VERIFICATION");
      const requestingUserSessionToken = getRequestingUserSessionToken();
      const adminToken = getAdminSessionToken();
      const isRequestingUserSessionExists = !!requestingUserSessionToken;
      const isAdminSessionExists = !!adminToken;
      if (
        userProfileQuery.data?.phone.status !==
          PhoneVerificationStatus.Verified &&
        canSkip !== "true" &&
        !(isRequestingUserSessionExists || isAdminSessionExists)
      ) {
        history.replace({
          pathname: "/member/verify/phone-number",
          state: {
            canSkip: true
          }
        });
      }

      onFinish();
    }
  );

  return <LoadingPlaceholder />;
};

export const MemberApp: React.FC<Props> = () => {
  const isDesktop = useIsDesktop();
  const userId = useUserId();
  const location = useLocation();
  const userProfileQuery = useUserProfile(userId);
  const activityQuery = useActivities(userId);
  const memberQuery = useMemberProfile(userId, {
    retry: false
  });
  const featureFlagsQuery = useFeatureFlags();

  const [isInitMemberAppDone, setIsInitMemberAppDone] = useState(false);
  const [memberStatus, setMemberStatus] = useState<"LOADING" | "EXIST" | "NEW">(
    "LOADING"
  );

  useEffect(() => {
    console.log(`INIT APP. MEMBER STATUS: ${memberStatus}`);
  }, [memberStatus]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const stuckQuery = [memberQuery, featureFlagsQuery, activityQuery].find(
        query => query.status === QueryStatus.Loading
      );

      if (stuckQuery) {
        stuckQuery.refetch();
      }
    }, 10000);

    return () => {
      clearTimeout(timeout);
    };
  }, [memberQuery.status, featureFlagsQuery.status, activityQuery.status]);

  useDidFinishQueries([memberQuery, featureFlagsQuery, activityQuery], () => {
    if (memberQuery.data) {
      setMemberStatus("EXIST");
    } else {
      setMemberStatus("NEW");
    }
  });

  if (memberQuery.isError && memberQuery.error?.response?.status !== 400) {
    return <>{RenderDefaultPageErrorMessage()}</>;
  }

  if (!isInitMemberAppDone) {
    if (memberStatus === "LOADING") {
      return <LoadingPlaceholder />;
    } else if (memberStatus === "EXIST") {
      return <InitMember onFinish={() => setIsInitMemberAppDone(true)} />;
    } else {
      return <CreateMember onFinish={() => setIsInitMemberAppDone(true)} />;
    }
  }

  return (
    <React.Fragment>
      <MemberPlaidModals />
      <Select2FAChannelFormModal />
      <SessionOTPModal />
      <VerificationUpgradeModal />
      <Switch>
        {/* Fullscreen page routes */}
        <Route
          path="/member/onboarding/block"
          exact
          component={OnboardingBlockForm}
        />
        <Route
          path="/member/onboarding/step/1"
          exact
          component={OnboardingStep1}
        />
        <Route
          path="/member/onboarding/step/2"
          exact
          component={OnboardingStep2}
        />
        <Route
          path="/member/onboarding/step/3/payments"
          exact
          component={OnboardingStep3Payments}
        />
        <Route
          path="/member/onboarding/step/3/bookkeeping"
          exact
          component={OnboardingStep3Bookkeeping}
        />
        <Route
          path="/member/onboarding/step/3/benefits"
          exact
          component={OnboardingStep3Benefits}
        />
        <Route
          path="/member/onboarding/step/4/payments"
          exact
          component={OnboardingStep4Payments}
        />
        <Route
          path="/member/onboarding/step/4/bookkeeping"
          exact
          component={OnboardingStep4Bookkeeping}
        />
        <Route
          path="/member/onboarding/step/4/benefits"
          exact
          component={OnboardingStep4Benefits}
        />
        <Route
          path="/member/authorize/2fa/email/confirm"
          exact
          component={EmailMagicLinkHandler}
        />
        <Route
          path="/member/authorize/2fa/email"
          component={EmailMagicLink2FAInstructions}
        />
        <Route
          path="/member/authorize/verification/email/confirm"
          component={CompleteEmailVerification}
        />
        <Route path="/member/verify/email" component={BeginEmailVerification} />
        <Route
          path="/member/verify/phone-number"
          exact
          component={VerifyPhoneNumber}
        />

        <Route
          path="/member/verify/phone-number/existing-account"
          exact
          component={VerifyPhoneNumberExistingAccount}
        />

        <Route path="/member/verify/phone-number/otp" component={VerifyOTP} />

        <Route
          path="/member/onboarding/verify-email"
          exact
          component={OnboardingEmailVerification}
        />

        <Route
          path="/member/onboarding/customizing"
          exact
          component={OnboardingCustomizing}
        />

        {isUnderWriterMember(
          activityQuery.data?.context as IActivityContext
        ) ? (
          <Route
            path="/member/:partner/subscription-plans/"
            component={NextGenSubscriptionPlans}
          />
        ) : null}
        <Route
          path="/member/membership-plans"
          component={MembershipPlansWithAddOns}
        />
        <Route
          path="/member/subscription/trial-success"
          component={MembershipTrialSuccess}
        />
        {!isDesktop && (
          <Route
            key="add-new-token"
            exact
            path="/member/settings/tokens/new"
            component={AddNewToken}
          />
        )}
        {!isDesktop && (
          <Route
            key="edit-api-token"
            exact
            path={`/member/settings/tokens/edit/:sessionId`}
            component={EditNewToken}
          />
        )}

        <Route
          path="*"
          children={
            <BaseAppLayout>
              <Switch>
                {/* Nav page routes */}
                <Route path="/member/dashboard" component={MemberDashboard} />
                <Route path="/member/taxes" component={Taxes} />
                <Route path="/member/invoices" component={Invoices} />
                <Route path="/member/1099-filing" component={NEC1099Filing} />
                <Route path="/member/benefits" component={BenefitsRoutes} />
                <Route path="/member/wallet" component={WalletIndex} />
                <Route
                  path="/member/organization"
                  component={OrganizationIndex}
                />
                <Route
                  path="/member/bookkeeping"
                  component={BookkeepingRoutes}
                />
                <Route path="/member/documents" exact component={Documents} />
                <Route
                  path="/member/knowledge-center"
                  component={KnowledgeCenterRoot}
                />
                <Route path="/member/settings" component={Settings} />
                <Route
                  path="/member/personal-pay-link"
                  component={PersonalPayLinkRoutes}
                />
                <Route
                  path="/member/membership-billing-history"
                  component={MembershipBillingHistory}
                />
                <Route
                  path="/member/bank-statements/:statementId"
                  component={MemberBankStatementModal}
                />
                <Route
                  path="/member/bank-statements"
                  component={MemberBankStatements}
                />
                {featureFlagsQuery.data?.bankingCards ? (
                  <Route
                    path="/member/banking/cards"
                    component={DebitCardsList}
                  />
                ) : null}
                <Route path="/member/sign-in" component={SignInRedirect} />
                <Route
                  path="/member/payments-security"
                  component={PaymentsSecurityScreen}
                />
                <Route path="/member/admin" component={Admin} />
                <Redirect
                  from="*"
                  to={
                    shouldEnforcePhoneVerification(
                      userProfileQuery.data as INewUser
                    )
                      ? `/member/verify/phone-number${location.hash}`
                      : "/member/dashboard"
                  }
                />
              </Switch>
            </BaseAppLayout>
          }
        />
      </Switch>
    </React.Fragment>
  );
};
